import { useState } from "react";
import { Layers } from "lucide-react";
import {
  LineChart,
  Line,
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import { FilterBar } from "@/components/dashboard/FilterBar";
import { ProductSelector } from "@/components/dashboard/ProductSelector";
import { VarianceHeatmap } from "@/components/charts/VarianceHeatmap";
import {
  useMaterialVariance,
  useMaterialTrend,
} from "@/hooks/use-materials";
import { useMaterialQuality } from "@/hooks/use-analytics";
import { useProductionProducts } from "@/hooks/use-production";
import { formatDate, daysAgo, today } from "@/lib/utils";
import { HelpIcon } from "@/components/HelpTooltip";
import { useSetPageHeader } from "@/components/layout/PageHeaderContext";

export function Materials() {
  useSetPageHeader({
    title: "Materials",
    subtitle: "Material variance tracking and analysis",
    icon: Layers,
  });

  const [from, setFrom] = useState(() => daysAgo(90));
  const [to, setTo] = useState(() => today());
  const [product, setProduct] = useState("");
  const [selectedMaterial, setSelectedMaterial] = useState("");

  const partsQuery = useProductionProducts();
  const qaSlug = product || null;
  const variance = useMaterialVariance(from, to, product || undefined);
  const trend = useMaterialTrend(
    selectedMaterial,
    from,
    to,
    product || undefined
  );

  return (
    <div className="space-y-6">
      <FilterBar from={from} to={to} onFromChange={setFrom} onToChange={setTo}>
        <ProductSelector
          products={
            partsQuery.data?.map((p) => ({
              value: p.slug,
              label: p.name,
            })) || []
          }
          value={product}
          onChange={setProduct}
        />
      </FilterBar>

      {/* Variance table */}
      <div className="rounded-xl border border-border bg-card p-5">
        <h2 className="mb-4 text-lg font-semibold text-card-foreground">
          Materials Ranked by Absolute % Variance
          <HelpIcon k="abs_pct" />
        </h2>
        {variance.isLoading ? (
          <p className="text-muted-foreground">Loading...</p>
        ) : (
          <VarianceHeatmap
            data={variance.data || []}
            onSelect={setSelectedMaterial}
            selected={selectedMaterial}
          />
        )}
      </div>

      {/* Material → Quality Correlation */}
      {selectedMaterial && qaSlug && (
        <MaterialQualitySection
          product={qaSlug}
          material={selectedMaterial}
          from={from}
          to={to}
        />
      )}

      {/* Trend chart for selected material */}
      {selectedMaterial && (
        <div className="rounded-xl border border-border bg-card p-5">
          <h2 className="mb-4 text-lg font-semibold text-card-foreground">
            Trend: {selectedMaterial}
          </h2>
          {trend.isLoading ? (
            <div className="flex h-60 items-center justify-center text-muted-foreground">
              Loading trend...
            </div>
          ) : trend.data && trend.data.length > 0 ? (
            <div className="h-60 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart
                  data={trend.data}
                  margin={{ top: 10, right: 20, bottom: 10, left: 10 }}
                >
                  <CartesianGrid strokeDasharray="3 3" className="opacity-20" />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 11 }}
                    tickFormatter={(d) => {
                      const date = new Date(d);
                      return `${date.getDate()}/${date.getMonth() + 1}`;
                    }}
                  />
                  <YAxis
                    tick={{ fontSize: 11 }}
                    label={{
                      value: "%",
                      angle: -90,
                      position: "insideLeft",
                      style: { fontSize: 11 },
                    }}
                  />
                  <Line
                    type="monotone"
                    dataKey="target_pct"
                    stroke="hsl(142, 71%, 45%)"
                    strokeWidth={2}
                    dot={false}
                    name="Target"
                  />
                  <Line
                    type="monotone"
                    dataKey="actual_pct"
                    stroke="hsl(220, 70%, 50%)"
                    strokeWidth={2}
                    dot={false}
                    name="Actual"
                  />
                  <Tooltip
                    content={({ active, payload, label }) => {
                      if (!active || !payload?.length) return null;
                      return (
                        <div className="rounded-lg border border-border bg-popover p-2 text-sm text-popover-foreground shadow-md">
                          <p className="font-medium">{label}</p>
                          {payload.map((p) => (
                            <p key={p.dataKey as string}>
                              {p.name}: {Number(p.value).toFixed(2)}%
                            </p>
                          ))}
                        </div>
                      );
                    }}
                  />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <p className="text-muted-foreground">No trend data available</p>
          )}
        </div>
      )}
    </div>
  );
}

function MaterialQualitySection({
  product,
  material,
  from,
  to,
}: {
  product: string;
  material: string;
  from: string;
  to: string;
}) {
  const [test, setTest] = useState("tensile_strength");
  const correlation = useMaterialQuality(product, material, test, from, to);

  const testOptions = [
    { value: "tensile_strength", label: "Tensile Strength" },
    { value: "impact_resistance", label: "Impact Resistance" },
    { value: "melt_flow_index", label: "Melt Flow Index" },
    { value: "hardness", label: "Hardness" },
  ];

  return (
    <div className="rounded-xl border border-border bg-card p-5">
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-card-foreground">
            Quality Correlation: {material}
          </h2>
          <p className="text-xs text-muted-foreground">
            Does material variance affect test outcomes? (daily averages)
          </p>
        </div>
        <select
          value={test}
          onChange={(e) => setTest(e.target.value)}
          className="rounded-md border border-input bg-background px-3 py-1.5 text-sm text-foreground"
        >
          {testOptions.map((t) => (
            <option key={t.value} value={t.value}>
              {t.label}
            </option>
          ))}
        </select>
      </div>

      {correlation.isLoading ? (
        <div className="flex h-60 items-center justify-center text-muted-foreground">
          Loading correlation data...
        </div>
      ) : correlation.data && correlation.data.data.length > 0 ? (
        <>
          {/* Correlation coefficient */}
          {correlation.data.correlation != null && (
            <div className="mb-4 flex items-center gap-3">
              <span className="text-sm text-muted-foreground">Correlation (r)<HelpIcon k="correlation_r" />:</span>
              <span
                className={`text-lg font-bold ${
                  Math.abs(correlation.data.correlation) >= 0.5
                    ? "text-amber-400"
                    : Math.abs(correlation.data.correlation) >= 0.3
                      ? "text-purple-400"
                      : "text-muted-foreground"
                }`}
              >
                {correlation.data.correlation.toFixed(3)}
              </span>
              <span className="text-xs text-muted-foreground">
                ({correlation.data.data.length} paired days)
              </span>
              <span className="text-xs text-muted-foreground">
                {Math.abs(correlation.data.correlation) >= 0.5
                  ? "Moderate-strong relationship"
                  : Math.abs(correlation.data.correlation) >= 0.3
                    ? "Weak-moderate relationship"
                    : "Weak/no relationship"}
              </span>
            </div>
          )}

          {/* Scatter chart */}
          <div className="h-60 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <ScatterChart margin={{ top: 10, right: 20, bottom: 10, left: 10 }}>
                <CartesianGrid strokeDasharray="3 3" className="opacity-20" />
                <XAxis
                  dataKey="variance_pct"
                  type="number"
                  tick={{ fontSize: 11 }}
                  name="Material Variance"
                  label={{ value: "Variance (%)", position: "insideBottom", offset: -5, style: { fontSize: 11 } }}
                />
                <YAxis
                  dataKey="test_value"
                  type="number"
                  tick={{ fontSize: 11 }}
                  name="Test Value"
                  label={{ value: testOptions.find((t) => t.value === test)?.label || test, angle: -90, position: "insideLeft", style: { fontSize: 11 } }}
                />
                <Scatter
                  data={correlation.data.data}
                  fill="hsl(220, 70%, 50%)"
                  opacity={0.7}
                />
                <Tooltip
                  content={({ active, payload }) => {
                    if (!active || !payload?.[0]) return null;
                    const d = payload[0].payload as { date: string; variance_pct: number; test_value: number; run_count: number; sample_count: number };
                    return (
                      <div className="rounded-lg border border-border bg-popover p-2 text-sm text-popover-foreground shadow-md">
                        <p className="font-medium">{formatDate(d.date)}</p>
                        <p>Variance: {d.variance_pct.toFixed(3)}%</p>
                        <p>Test value: {d.test_value.toFixed(2)}</p>
                        <p className="text-xs text-muted-foreground">
                          {d.run_count} run{d.run_count > 1 ? "s" : ""}, {d.sample_count} sample{d.sample_count > 1 ? "s" : ""}
                        </p>
                      </div>
                    );
                  }}
                />
              </ScatterChart>
            </ResponsiveContainer>
          </div>
        </>
      ) : correlation.data ? (
        <div className="flex h-40 items-center justify-center text-muted-foreground">
          No overlapping data — no days where both material usage and QA samples exist
        </div>
      ) : null}
    </div>
  );
}
